// Dead-heat scoring for implied odds.
//
// Each dataset ("count") is treated as a single race: the entrant with the most
// votes wins it. When n entrants tie at the top, each receives a 1/n share of
// that win instead of a full win, so the shares handed out per count always sum
// to exactly 1. Fair odds are then 1 / (effective wins / counts).
//
//   5 outright wins + 1 two-way tie over 8 counts
//   => 5.5 / 8 = 68.75%  =>  $1.45

// Tolerance for float drift (e.g. handicap-adjusted totals like 21.19 + 1.5).
const EPS = 1e-9;

const isEqual = (a, b) => Math.abs(a - b) < EPS;

/**
 * Share of a single count's win for an entrant with `value`, given the values
 * of every entrant in that count. Returns 1 for an outright win, 1/n for an
 * n-way tie at the top, and 0 otherwise (including when nobody has votes).
 */
export const deadHeatShare = (value, values) => {
  let max = -Infinity;
  let tied = 0;
  for (const raw of values) {
    const v = Number(raw) || 0;
    if (v > max + EPS) {
      max = v;
      tied = 1;
    } else if (isEqual(v, max)) {
      tied += 1;
    }
  }
  if (!(max > 0) || tied === 0) return 0;
  return isEqual(Number(value) || 0, max) ? 1 / tied : 0;
};

/**
 * Same as deadHeatShare, for callers that have already precomputed the top
 * value and how many entrants sit on it.
 */
export const deadHeatShareFromMax = (value, max, tiedCount) => {
  if (!(max > 0) || !tiedCount) return 0;
  return isEqual(Number(value) || 0, max) ? 1 / tiedCount : 0;
};

/**
 * Dead-heat share of a "Top N" place for an entrant ranked `rank` (competition
 * ranking: tied entrants share the best rank, e.g. two players at =10) whose
 * tie group has `tieCount` members.
 *
 * If the whole tie group fits inside the cut-off the share is 1. If the group
 * straddles the cut-off, the places left inside it (N - rank + 1) are split
 * evenly across the group. Two players at =10 for Top 10 -> 0.5 each; three
 * at =9 for Top 10 -> 2/3 each. Ranked outside the cut-off -> 0.
 */
export const deadHeatPlaceShare = (rank, tieCount, limit) => {
  if (!rank || rank > limit) return 0;
  const n = Math.max(1, tieCount || 1);
  const placesLeft = limit - rank + 1;
  return Math.min(1, placesLeft / n);
};

/** Number of entries equal to `max` (with float tolerance). */
export const countAtMax = (values, max) => {
  let n = 0;
  for (const raw of values) {
    if (isEqual(Number(raw) || 0, max)) n += 1;
  }
  return n;
};

/**
 * Display helper for effective wins: "5", "5.5", "5.33". Rounds to two
 * decimals and drops trailing zeros so integer counts still read as before.
 */
export const formatWins = (wins) => String(Math.round((Number(wins) || 0) * 100) / 100);

/**
 * Display helper for hit-rate percentages: "62.5", "18.75", "100". Up to two
 * decimals with trailing zeros dropped, so 1/8 reads as 12.5% not 13%.
 */
export const formatPct = (pct) => String(Math.round((Number(pct) || 0) * 100) / 100);
